import { BRAND } from '@sb2/shared';
import { useLocale } from '../../i18n/LocaleContext';

export type ShareRunStats = {
  mode: string;
  score: number;
  bestStreak: number;
  correct?: number;
  wrong?: number;
  timeouts?: number;
  durationSec?: number | null;
};

function modeLabel(
  mode: string,
  t: (k: 'soloTimed' | 'soloLives' | 'soloTimeBank' | 'soloDaily' | 'lbMultiplayer') => string,
): string {
  if (mode === 'solo_lives') return t('soloLives');
  if (mode === 'solo_timebank') return t('soloTimeBank');
  if (mode === 'solo_daily') return t('soloDaily');
  if (mode === 'mp_timed') return t('lbMultiplayer');
  return t('soloTimed');
}

export function ShareRunCard({ stats }: { stats: ShareRunStats }) {
  const { t } = useLocale();
  const isLives = stats.mode === 'solo_lives';
  const headline = isLives ? stats.bestStreak : stats.score;
  const headlineLabel = isLives ? t('soloBestStreak') : t('soloScore');
  const secondary = isLives
    ? `${t('soloScore')} ${stats.score}`
    : `${t('soloBestStreak')} ${stats.bestStreak}`;

  const shareText = [
    `${BRAND.name} · ${modeLabel(stats.mode, t)}`,
    `${headlineLabel}: ${headline}`,
    secondary,
    stats.durationSec ? `${stats.durationSec}s` : null,
    typeof location !== 'undefined' ? location.origin : 'https://injurytime.xyz',
  ]
    .filter(Boolean)
    .join('\n');

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: BRAND.name, text: shareText });
        return;
      }
    } catch {
      // fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(shareText);
    } catch {
      // ignore
    }
  };

  return (
    <div className="share-card">
      <div className="share-card-face">
        <span className="share-card-brand">{BRAND.logo}</span>
        <span className="share-card-mode">{modeLabel(stats.mode, t)}</span>
        <strong className="share-card-score">{headline}</strong>
        <span className="share-card-label">{headlineLabel}</span>
        <span className="share-card-sub">{secondary}</span>
      </div>
      <button type="button" className="btn" onClick={() => void share()}>
        {t('soloShare')}
      </button>
    </div>
  );
}
